import React, { useEffect, useMemo, useState } from 'react'
import { graphql } from 'relay-runtime'
import { useFragment, useMutation } from 'react-relay'
import { CreativeTextForm$key } from './__generated__/CreativeTextForm.graphql'
import { useForm } from 'react-hook-form'
import FormTextField from '../../components/FormTextField'
import FormSelect, {
  enumStringsToReactSelectOptions,
  enumStringToReactSelectOption,
  ReactSelectOption,
} from '../../components/FormSelect'
import { Button } from '@mui/material'
import styled from '@emotion/styled'
import {
  CreativeTextFormCreateMetaAdMutation,
  CreativeTextFormCreateMetaAdMutation$data,
} from './__generated__/CreativeTextFormCreateMetaAdMutation.graphql'
import {
  CreativeTextFormUpdateMetaAdMutation,
  CreativeTextFormUpdateMetaAdMutation$data,
} from './__generated__/CreativeTextFormUpdateMetaAdMutation.graphql'
import { toast } from 'react-toastify'
import { formActionVerb, FormModalAction } from '../../components/FormModal'
import { useAppDispatch } from '../../state/hooks'
import { setCreativeDrawerData } from '../../state/appSlice'
import { CreativeDrawerView } from './CreativeDrawer'
import { getMetaAdListQueryVariables } from '../../pages/HomeRoute'
import { CreativeTextFormUser$key } from './__generated__/CreativeTextFormUser.graphql'
import AdSetAsyncMultiSelect, {
  adSetToSelectOption,
} from '../targeting/AdSetAsyncMultiSelect'
import { CreativeTextFormBrand$key } from './__generated__/CreativeTextFormBrand.graphql'
import { useGlobalState } from '../../state/v2/store'
import { useTypeParam } from '../../state/url/type'

interface CreativeTextFormProps {
  brandFragmentRequest: CreativeTextFormBrand$key
  metaAdFragmentRequest: CreativeTextForm$key | null
  userFragmentRequest: CreativeTextFormUser$key
  setPendingChanges: (pendingChanges: boolean) => void
  drawerOpen: boolean
}

export const creativeTextFormBrandFragment = graphql`
  fragment CreativeTextFormBrand on Brand {
    facebookPages {
      id
      name
    }
    instagramAccounts {
      id
      name
    }
  }
`

export const creativeTextFormUserFragment = graphql`
  fragment CreativeTextFormUser on User {
    account {
      brands {
        id
        name
        facebookPages {
          id
          externalId
          name
          picture
        }
        instagramAccounts {
          id
          externalId
          name
          picture
        }
      }
    }
  }
`

export const creativeTextFormFragment = graphql`
  fragment CreativeTextForm on MetaAd {
    id
    callToAction
    name
    description
    destinationUrl
    displayUrl
    headline
    primaryText
    facebookPage {
      id
      name
      picture
    }
    instagramAccount {
      id
      name
      picture
    }
    metaAdMetaAdSets {
      metaAdSet {
        id
        name
        campaignName
        metaAdAccount {
          id
          name
        }
        effectiveStatus
        status
      }
    }
  }
`

const creativeTextFormCreateMetaAdMutation = graphql`
  mutation CreativeTextFormCreateMetaAdMutation(
    $input: CreateMetaAdRequest!
    $metaAdsFilter: MetaAdsFilter
  ) {
    createMetaAd(input: $input, metaAdsFilter: $metaAdsFilter) {
      id
      primaryText
      headline
      name
      description
      destinationUrl
      displayUrl
      callToAction
      facebookPageId
      instagramAccountId
      ...MetaAd
      ...MetaAdPreview
      ...CreativeDrawerMetaAd
      brand {
        id
        draftMetaAdCount
        ...SubmitCreativeForApprovalConfirmDialogBrand
        ...CreativeList
      }
    }
  }
`

const creativeTextFormUpdateMetaAdMutation = graphql`
  mutation CreativeTextFormUpdateMetaAdMutation(
    $input: UpdateMetaAdRequest!
    $metaAdsFilter: MetaAdsFilter
  ) {
    updateMetaAd(input: $input, metaAdsFilter: $metaAdsFilter) {
      id
      primaryText
      headline
      name
      description
      destinationUrl
      displayUrl
      callToAction
      facebookPageId
      instagramAccountId
      ...MetaAd
      ...MetaAdPreview
      ...CreativeDrawerMetaAd
      brand {
        id
        draftMetaAdCount
        ...SubmitCreativeForApprovalConfirmDialogBrand
        ...CreativeList
      }
    }
  }
`

interface CreativeTextFormValues {
  primaryText: string
  headline: string
  name: string
  description: string
  destinationUrl: string
  displayLink: string
  callToAction?: ReactSelectOption
  facebookPage?: ReactSelectOption
  instagramAccount?: ReactSelectOption
  adSets?: ReactSelectOption[]
}

const callToActionOptions = enumStringsToReactSelectOptions([
  'CONTACT_US',
  'GET_QUOTE',
  'LEARN_MORE',
  'SIGN_UP',
  'SHOP_NOW',
  'SUBSCRIBE',
])

const S = {
  Wrapper: styled.div`
    width: max-content;
  `,
  Form: styled.form`
    display: flex;
    flex-direction: column;
    margin-right: 0.5rem;
    width: 20rem;
    & > * {
      width: 100%;
      margin-bottom: 1rem !important;
    }
  `,
  Actions: styled.div`
    display: flex;
    justify-content: right;
    margin-right: 0.5rem;
  `,
  FormHeader: styled.h4`
    margin-top: 0.5rem;
  `,
  Subtext: styled.span`
    margin-top: -0.5rem;
    color: grey;
    font-size: 0.675rem;
  `,
}

const CreativeTextForm = ({
  brandFragmentRequest,
  metaAdFragmentRequest,
  userFragmentRequest,
  setPendingChanges,
  drawerOpen,
}: CreativeTextFormProps) => {
  const brand = useFragment(creativeTextFormBrandFragment, brandFragmentRequest)
  const metaAd = useFragment(creativeTextFormFragment, metaAdFragmentRequest)
  const user = useFragment(creativeTextFormUserFragment, userFragmentRequest)
  const defaultValues = useMemo(
    () => ({
      facebookPage:
        metaAd != null && metaAd.facebookPage != null
          ? {
              value: metaAd.facebookPage!.id,
              label: metaAd.facebookPage!.name,
            }
          : undefined,
      instagramAccount:
        metaAd != null && metaAd.instagramAccount != null
          ? {
              value: metaAd.instagramAccount!.id,
              label: metaAd.instagramAccount!.name,
            }
          : undefined,
      name: metaAd != null && metaAd.name != null ? metaAd.name : '',
      primaryText:
        metaAd != null && metaAd.primaryText != null ? metaAd.primaryText : '',
      headline:
        metaAd != null && metaAd.headline != null ? metaAd.headline : '',
      description:
        metaAd != null && metaAd.description != null ? metaAd.description : '',
      destinationUrl:
        metaAd != null && metaAd.destinationUrl != null
          ? metaAd.destinationUrl
          : '',
      displayLink:
        metaAd != null && metaAd.displayUrl != null ? metaAd.displayUrl : '',
      callToAction:
        metaAd != null && metaAd.callToAction != null
          ? enumStringToReactSelectOption(metaAd.callToAction)
          : undefined,
      adSets:
        metaAd != null && metaAd.metaAdMetaAdSets != null
          ? metaAd.metaAdMetaAdSets.map((metaAdMetaAdSet) =>
              adSetToSelectOption(metaAdMetaAdSet.metaAdSet)
            )
          : [],
    }),
    [metaAd]
  )
  const form = useForm({ defaultValues })
  const [createMetaAdRequest, createMetaAdLoading] =
    useMutation<CreativeTextFormCreateMetaAdMutation>(
      creativeTextFormCreateMetaAdMutation
    )
  const [updateMetaAdRequest, updateMetaAdLoading] =
    useMutation<CreativeTextFormUpdateMetaAdMutation>(
      creativeTextFormUpdateMetaAdMutation
    )
  const dispatch = useAppDispatch()
  const brandId = useGlobalState((b) => b.brand?.map((brand) => brand.id).join(","))
  const [type] = useTypeParam()
  const facebookPageOptions = brand.facebookPages.map(({ id, name }) => ({
    value: id,
    label: name,
  }))
  const instagramAccountOptions = brand.instagramAccounts.map(
    ({ id, name }) => ({ value: id, label: name })
  )

  useEffect(() => {
    if (!drawerOpen) {
      form.reset(defaultValues)
    }
  }, [form, defaultValues, drawerOpen])

  const {
    formState: { isDirty },
  } = form
  useEffect(() => {
    setPendingChanges(isDirty)
  }, [isDirty, setPendingChanges])

  const action = metaAd == null ? FormModalAction.CREATE : FormModalAction.EDIT

  const onSubmit = (values: CreativeTextFormValues) => {
    const createAndUpdateInput = {
      primaryText: values.primaryText,
      headline: values.headline,
      name: values.name,
      description: values.description,
      destinationUrl: values.destinationUrl,
      displayUrl: values.displayLink,
    } as any
    if (values.facebookPage != null) {
      createAndUpdateInput.facebookPageId = values.facebookPage.value
    } else {
      createAndUpdateInput.facebookPageId = null
    }
    if (values.instagramAccount != null) {
      createAndUpdateInput.instagramAccountId = values.instagramAccount.value
    } else {
      createAndUpdateInput.instagramAccountId = null
    }
    if (values.callToAction != null) {
      createAndUpdateInput.callToAction = values.callToAction.value
    }
    if (values.adSets != null) {
      createAndUpdateInput.metaAdSetIds = values.adSets.map(
        (adSet) => adSet.value
      )
    }
    const onCompleted = (
      result:
        | CreativeTextFormCreateMetaAdMutation$data
        | CreativeTextFormUpdateMetaAdMutation$data
    ) => {
      toast.success(`Successfully ${formActionVerb(action)} ad.`)
      setPendingChanges(false)
      form.reset(values)
      if (action === FormModalAction.CREATE) {
        dispatch(
          setCreativeDrawerData({
            metaAdFragmentRequest: (
              result as CreativeTextFormCreateMetaAdMutation$data
            ).createMetaAd,
            view: CreativeDrawerView.TEXT,
          })
        )
      }
    }
    const onError = (error: Error) => {
      console.log(error)
      toast.error(`Unable to ${action.toLowerCase()} ad.`)
    }
    if (action === FormModalAction.CREATE) {
      createMetaAdRequest({
        variables: {
          input: {
            ...createAndUpdateInput,
            brandId,
          },
          ...getMetaAdListQueryVariables(type),
        },
        onCompleted,
        onError,
      })
    } else {
      updateMetaAdRequest({
        variables: {
          input: {
            ...createAndUpdateInput,
            id: metaAd!.id!,
          },
        },
        onCompleted,
        onError,
      })
    }
  }
  const validatePrimaryText = {
    'more than 1000 characters': (value: string) => value.length <= 1000,
  }
  const validateHeadline = {
    'more than 40 characters': (value: string) => value.length <= 40,
  }
  const validateDescription = {
    'more than 30 characters': (value: string) => value.length <= 30,
  }
  const validateName = {
    'more than 1000 characters': (value: string) => value.length <= 1000,
  }
  const validateUrl = {
    'not a URL (include https://)': (value: string) =>
      value === '' || /^(http|https):\/\/[^ "]+$/.test(value),
  }
  const defaultLabelText = "Primary Text";
  const [labelText, setLabelText] = useState(defaultLabelText);

  return (
    <S.Wrapper>
      <S.Form onSubmit={form.handleSubmit(onSubmit)}>
        <S.FormHeader>Brand Accounts *</S.FormHeader>
        <S.Subtext>
          * At least one Facebook Page OR Instagram Account is required.
        </S.Subtext>
        <FormSelect
          useForm={form}
          label="Facebook Page"
          name="facebookPage"
          options={facebookPageOptions}
        />
        <FormSelect
          useForm={form}
          label="Instagram Account"
          name="instagramAccount"
          options={instagramAccountOptions}
        />

        <S.FormHeader>Ad Details</S.FormHeader>
        <FormTextField
          useForm={form}
          label="Name (Optional)"
          name="name"
          rules={{ validate: validateName }}
        />
        <FormTextField
          useForm={form}
          label={labelText}
          name="primaryText"
          required
          multiLine
          rules={{ validate: validatePrimaryText }}
          onChange={(event) => {
            const newText = event.target.value;
            const maxLength = 125;
            const isExceedingMaxLength = newText.length > maxLength;
            const newLabel = isExceedingMaxLength ? `Primary Text is more than ${maxLength} characters` : defaultLabelText;
            setLabelText(newLabel);
            form.setValue("primaryText", newText);
          }}
        />
        <FormSelect
          useForm={form}
          label="Call to Action"
          name="callToAction"
          required
          options={callToActionOptions}
        />
        <FormTextField
          useForm={form}
          label="Destination URL"
          name="destinationUrl"
          required
          multiLine
          rules={{ validate: validateUrl }}
        />
        <FormTextField
          useForm={form}
          label="Headline (Optional)"
          name="headline"
          rules={{ validate: validateHeadline }}
        />
        <FormTextField
          useForm={form}
          label="Description (Optional)"
          name="description"
          rules={{ validate: validateDescription }}
        />
        <FormTextField
          useForm={form}
          label="Display Link (Optional)"
          name="displayLink"
          rules={{ validate: validateUrl }}
        />

        <S.FormHeader>Ad Sets</S.FormHeader>
        <AdSetAsyncMultiSelect useForm={form} />

        <S.Actions>
          <Button
            variant="contained"
            type="submit"
            disabled={createMetaAdLoading || updateMetaAdLoading}
          >
            Save
          </Button>
        </S.Actions>
      </S.Form>
    </S.Wrapper>
  )
}

export default CreativeTextForm
